<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PipelineCardSkeleton from './PipelineCardSkeleton.vue';

const props = defineProps({
  columns: {
    type: Number,
    default: 4,
    validator: v => v >= 1 && v <= 10,
  },
  cardsPerColumn: {
    type: [Number, Array],
    default: 3,
    validator: v => {
      if (typeof v === 'number') return v >= 0 && v <= 10;
      if (Array.isArray(v)) return v.every(n => n >= 0 && n <= 10);
      return false;
    },
  },
});

const columnCardCounts = computed(() => {
  if (Array.isArray(props.cardsPerColumn)) {
    return Array.from(
      { length: props.columns },
      (_, i) => props.cardsPerColumn[i] ?? 3
    );
  }
  return Array.from({ length: props.columns }, (_, i) => {
    const baseCount = props.cardsPerColumn;
    const variations = [baseCount, baseCount - 1, baseCount + 1, baseCount];
    return Math.max(1, variations[i % variations.length]);
  });
});

const { t } = useI18n();
</script>

<template>
  <div class="flex min-h-[400px] gap-4 overflow-x-auto p-3">
    <span class="sr-only" role="status">{{ t('PIPELINE.LOADING_ITEMS') }}</span>
    <div
      v-for="(cardCount, colIndex) in columnCardCounts"
      :key="colIndex"
      class="flex w-80 min-w-[280px] max-w-[350px] flex-none animate-pulse flex-col rounded-xl bg-n-alpha-1 motion-reduce:animate-none"
      aria-hidden="true"
    >
      <div
        class="flex items-center justify-between px-3 py-3 border-b border-n-weak"
      >
        <div class="flex items-center gap-2">
          <div class="h-2.5 w-2.5 rounded-full bg-n-alpha-3" />
          <div class="h-4 w-20 rounded bg-n-alpha-3" />
          <div class="h-5 w-6 rounded-full bg-n-alpha-3" />
        </div>
        <div class="flex gap-1">
          <div class="h-7 w-7 rounded-lg bg-n-alpha-3" />
        </div>
      </div>

      <div class="flex-1 flex flex-col gap-2 p-2 min-h-[200px] overflow-hidden">
        <PipelineCardSkeleton v-for="cardIndex in cardCount" :key="cardIndex" />
      </div>

      <div class="px-3 py-2 border-t border-n-weak">
        <div class="h-9 w-full rounded-lg bg-n-alpha-3" />
      </div>
    </div>
  </div>
</template>
